import Combobox from "@/components/Combobox";
import FormHeader from "@/components/FormHeader";
import InputError from "@/components/InputError";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { listKurikulum } from "@/constants";
import api from "@/lib/axios";
import { DataNilaiRapor } from "@/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  BookOpen,
  FileText,
  Hash,
  Loader2,
  UploadCloud,
  CheckCircle2,
  Eye,
  AlertTriangle,
} from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";

const postData = async (formData: FormData) => {
  const response = await api.post(
    "/siswa/penerimaan/smbjm/nilai-rapor/store",
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return response.data;
};

export default function Form({
  selectedRow,
  closeModal,
  idDataPendaftaran,
}: {
  selectedRow?: DataNilaiRapor | null;
  closeModal: () => void;
  idDataPendaftaran?: string;
}) {
  const [form, setForm] = useState<DataNilaiRapor>({
    id_data_pendaftaran: idDataPendaftaran || "",
    id_data_nilai_rapor: selectedRow?.id_data_nilai_rapor || "",
    rata_rata_nilai: selectedRow?.rata_rata_nilai || "",
    file_rapor_semester: selectedRow?.file_rapor_semester || "",
    semester: selectedRow?.semester || 1,
    kurikulum: selectedRow?.kurikulum || "",
  });

  const [file, setFile] = useState<File | null>(null);
  const [openKurikulum, setOpenKurikulum] = useState(false);

  const queryClient = useQueryClient();
  const { mutate, isPending, error } = useMutation({
    mutationFn: postData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listNilaiRapor"] });
      queryClient.invalidateQueries({ queryKey: ["dataStep"] });
      closeModal();
      toast.success(data?.message || "Data berhasil disimpan.");
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      toast.error(err.response?.data?.message || "Gagal menyimpan data.");
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) setFile(file);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData();
    formData.append("id_data_pendaftaran", form.id_data_pendaftaran);
    formData.append("semester", String(form.semester));
    formData.append("rata_rata_nilai", String(form.rata_rata_nilai));
    formData.append("kurikulum", String(form.kurikulum));
    if (file) formData.append("file", file);
    mutate(formData);
  };

  // Helper boolean untuk mengecek apakah sudah ada file di database
  const hasExistingFile = !!form.file_rapor_semester;

  return (
    <form onSubmit={handleSubmit} className="p-6">
      <FormHeader
        icon={<BookOpen className="text-blue-600" />}
        title={`Input Nilai Rapor - Semester ${selectedRow?.semester}`}
        description="Pastikan data nilai yang dimasukkan sesuai dengan rapor asli."
      />

      <Separator className="my-6" />

      <div className="space-y-6">
        {/* KURIKULUM & NILAI */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Kurikulum */}
          <div className="space-y-2">
            <Label required>Kurikulum</Label>
            <Combobox
              open={openKurikulum}
              setOpen={setOpenKurikulum}
              value={form.kurikulum || ""}
              setValue={(value) =>
                setForm((prev) => ({ ...prev, kurikulum: value }))
              }
              options={listKurikulum}
              placeholder="Pilih Kurikulum"
            />
            <InputError
              text={error?.response?.data?.errors?.kurikulum?.join(",")}
            />
          </div>

          {/* Nilai Rapor */}
          <div className="space-y-2">
            <Label required>Rata-rata Nilai Pengetahuan</Label>
            <div className="relative">
              <Hash className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                value={form.rata_rata_nilai}
                placeholder="0.00"
                className="pl-9 font-mono font-medium"
                onChange={(event) => {
                  let val = event.target.value.replace(",", ".");

                  // Validasi 1: Hanya angka dan maksimal 2 digit setelah titik
                  if (/^\d*\.?\d{0,2}$/.test(val)) {
                    // Validasi 2: Pastikan nilainya tidak lebih dari 100
                    // Kita izinkan string kosong ("") atau cuma titik (".") agar user bisa menghapus/mengetik
                    const numericVal = parseFloat(val);
                    if (val === "" || val === "." || numericVal <= 100) {
                      setForm((prev) => ({ ...prev, rata_rata_nilai: val }));
                    }
                  }
                }}
                onBlur={() => {
                  setForm((prev) => ({
                    ...prev,
                    rata_rata_nilai: prev.rata_rata_nilai
                      ? Number(prev.rata_rata_nilai)
                      : "",
                  }));
                }}
              />
            </div>
            <p className="text-[10px] text-slate-500">
              Contoh: 85.50 (Maksimal 100, gunakan titik untuk desimal)
            </p>
            <InputError
              text={error?.response?.data?.errors?.rata_rata_nilai?.join(",")}
            />
          </div>
        </div>

        {/* UPLOAD FILE */}
        <div className="space-y-3 pt-2">
          <Label
            required={!hasExistingFile}
            className="flex items-center flex-wrap justify-between text-slate-900 dark:text-slate-100"
          >
            <div>
              Scan Rapor Asli
              <span className="ml-2 font-bold text-primary">
                Semester {selectedRow?.semester}
              </span>
            </div>
            <Badge
              variant="outline"
              className="text-[10px] h-5 font-normal dark:border-slate-700 dark:text-slate-300"
            >
              PDF Only
            </Badge>
          </Label>

          {/* Tampilan jika sudah ada file di database & user BELUM memilih file baru */}
          {hasExistingFile && !file && (
            <div className="flex items-center justify-between p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl mb-3">
              <div className="flex items-center gap-3">
                <div className="bg-emerald-100 dark:bg-emerald-800/50 p-2 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">
                    File Rapor Tersimpan
                  </p>
                  <p className="text-xs text-emerald-600 dark:text-emerald-500/80">
                    Anda sudah mengunggah file sebelumnya.
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="text-xs bg-white dark:bg-slate-900"
                onClick={() => window.open(form.file_rapor_semester, "_blank")}
                type="button"
              >
                <Eye className="w-4 h-4 mr-2" /> Lihat File
              </Button>
            </div>
          )}

          {/* Area Upload/Ganti File */}
          <div
            className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-colors ${file
              ? "border-blue-400 bg-blue-50/50 dark:border-blue-500/50 dark:bg-blue-900/20"
              : "border-slate-200 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:hover:border-slate-600 dark:hover:bg-slate-900/50"
              }`}
          >
            <Input
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              required={!hasExistingFile}
            />
            <div className="flex flex-col items-center gap-2 pointer-events-none">
              {file ? (
                <>
                  <FileText className="w-8 h-8 text-blue-500 dark:text-blue-400" />
                  <div className="text-sm font-medium text-blue-700 dark:text-blue-300 line-clamp-1">
                    {file.name}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    {(file.size / 1024).toFixed(0)} KB
                  </div>
                  <div className="text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 px-2 py-0.5 rounded mt-1">
                    {hasExistingFile
                      ? "Akan menggantikan file lama"
                      : "Siap diunggah"}
                  </div>
                </>
              ) : (
                <>
                  <UploadCloud className="w-8 h-8 text-slate-400 dark:text-slate-500" />
                  <div className="text-sm text-slate-600 dark:text-slate-300">
                    <span className="font-semibold text-blue-600 dark:text-blue-400">
                      Klik untuk upload
                    </span>{" "}
                    atau drag and drop
                  </div>
                  <div className="text-xs text-slate-400 dark:text-slate-500">
                    {hasExistingFile
                      ? "Unggah file baru jika ingin mengubah file yang sudah ada"
                      : "Maksimal ukuran file 2 MB"}
                  </div>
                </>
              )}
            </div>
          </div>
          <InputError text={error?.response?.data?.errors?.file?.join(",")} />
        </div>
      </div>

      <div className="mt-6 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-lg p-4 flex gap-3 items-start">
        <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800 dark:text-amber-200">
          <span className="font-semibold block mb-1">Penting:</span>
          Pastikan file yang diunggah adalah hasil scan <strong>rapor asli semua mata pelajaran</strong><span className="ml-2 font-bold text-primary">
            Semester {selectedRow?.semester}
          </span> yang telah ditandatangani oleh Kepala Sekolah dan dibubuhi cap basah resmi sekolah.
        </div>
      </div>

      <div className="mt-8 flex gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={closeModal}
          disabled={isPending}
          className="flex-1"
        >
          Batal
        </Button>
        <Button className="flex-1" disabled={isPending} type="submit">
          {isPending ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            "Simpan Data"
          )}
        </Button>
      </div>
    </form>
  );
}
